import { useEffect, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { ChevronLeft, Delete } from 'baseui/icon'
import prefersReducedMotion from '@/shared/tools/prefersReducedMotion'
import { useRightPanel, useRightPanelHopTimus } from './rightPanelContext'

const TYPING_TAGS = ['INPUT', 'TEXTAREA', 'SELECT']

const isTypingIn = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || TYPING_TAGS.includes(target.tagName))

const useRightPanelToggle = (toggleRef: RefObject<HTMLElement>) => {
  const { t } = useTranslation()
  const [, theme] = useStyletron()
  const { expanded, toggle } = useRightPanel()
  const hopTimus = useRightPanelHopTimus()
  const closesHopTimus = hopTimus.mode === 'docked'
  const showsCross = closesHopTimus || expanded

  const onToggle = () => {
    if (closesHopTimus) {
      hopTimus.close()
      return
    }
    toggle()
    if (expanded) return
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth'
    window.requestAnimationFrame(() => toggleRef.current?.scrollIntoView({ behavior, block: 'nearest' }))
  }

  useEffect(() => {
    if (!showsCross) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || isTypingIn(event.target)) return
      if (document.querySelector('[aria-modal="true"], [role="dialog"], [role="listbox"], [role="menu"]')) return
      if (closesHopTimus) {
        hopTimus.close()
        return
      }
      toggle()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [showsCross, closesHopTimus, hopTimus, toggle])

  const arrowLabel = t(expanded ? 'common:collapse_panel' : 'common:expand_panel')

  return {
    expanded,
    showsCross,
    onToggle,
    Icon: showsCross ? Delete : ChevronLeft,
    iconColor: theme.colors.primary,
    label: closesHopTimus ? t('common:hop_timus_close') : arrowLabel,
  }
}

export default useRightPanelToggle
